/**
 * =============================================================================
 * movePageToSection.ts
 * =============================================================================
 *
 * Utility per spostare una pagina (con tutte le sue sotto-pagine, sotto-sotto-
 * pagine, ecc.) da una Sezione di origine a una Sezione di destinazione, dentro
 * lo stesso corso.
 *
 * Modello dati di riferimento (cfr. NestedPageTree / StructureEditor):
 *   - `pages`            → entità pagina; `parent_page_id` definisce la
 *                          gerarchia padre/figlio; `section_id` è una colonna
 *                          informativa duplicata.
 *   - `section_pages`    → tabella ponte sezione↔pagina con `order_index`
 *                          (fonte di verità per l'ordine all'interno di una
 *                          sezione). OGNI pagina del sotto-albero possiede una
 *                          riga in `section_pages` per la sua sezione corrente.
 *
 * Cosa fa questa funzione:
 *   1. Calcola l'intero sotto-albero della pagina spostata (BFS sui figli via
 *      `parent_page_id`) leggendo le pagine della sezione di origine.
 *   2. Per OGNI nodo del sotto-albero, sposta la sua riga `section_pages` dalla
 *      sezione di origine a quella di destinazione (UPDATE atomico per riga).
 *   3. La radice spostata diventa root nella destinazione: viene azzerato
 *      `parent_page_id` e aggiornato `section_id` per coerenza informativa.
 *      Anche i discendenti vengono aggiornati nella loro colonna `section_id`.
 *      `parent_page_id` dei discendenti NON cambia: la gerarchia interna è
 *      preservata integralmente, niente nodi né contenuti vengono persi.
 *   4. Rinumera in modo sequenziale (0..N) gli `order_index` di:
 *        - tutti i fratelli RIMASTI nella sezione di origine al livello
 *          del nodo spostato (stesso `parent_page_id` originale);
 *        - tutte le pagine ROOT della sezione di destinazione, con il nuovo
 *          nodo aggiunto in coda (oppure, se passato `afterPageId`, subito
 *          dopo quella pagina nella destinazione).
 *
 * NOTA: la funzione esegue le scritture sequenzialmente perché non disponiamo
 * di transazioni client-side; in caso di errore intermedio, restituisce subito
 * un errore e l'eventuale stato parziale è comunque consistente perché ogni
 * pagina ha esattamente UNA riga in `section_pages` per il suo sotto-albero
 * (non si creano duplicati).
 * =============================================================================
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { withRpcRetry } from "./rpcRetry";

export interface MovePageToSectionParams {
  /** Client Supabase già autenticato (passato per facilitarne il testing). */
  supabase: SupabaseClient<any>;
  /** ID della pagina (radice del sotto-albero) da spostare. */
  pageId: string;
  /** Sezione di origine in cui si trova attualmente la pagina. */
  sourceSectionId: string;
  /** Sezione di destinazione: deve essere diversa da `sourceSectionId`. */
  destSectionId: string;
  /**
   * (Opzionale) ID di una pagina ROOT della sezione di destinazione: il nodo
   * spostato verrà inserito subito dopo questa. Se omesso, viene messo in coda.
   */
  afterPageId?: string | null;
  /**
   * (Opzionale) ID del corso atteso. Se passato, la funzione verifica che
   * sia la sezione di origine sia quella di destinazione appartengano a un
   * modulo dello stesso corso. Errore esplicito altrimenti.
   */
  expectedCourseId?: string | null;
}

export interface MovePageToSectionResult {
  /** Numero totale di nodi (pagina radice + discendenti) spostati. */
  movedCount: number;
  /** Informazioni necessarie per annullare lo spostamento. */
  undo: MoveUndoInfo;
}

/**
 * Informazioni di stato pre-spostamento, sufficienti a ricostruire la
 * collocazione originale (sezione, parent, posizione tra fratelli).
 */
export interface MoveUndoInfo {
  pageId: string;
  /** Sezione in cui la pagina si trova DOPO lo spostamento. */
  destSectionId: string;
  /** Sezione in cui la pagina si trovava PRIMA dello spostamento. */
  originalSectionId: string;
  /** parent_page_id originale della pagina spostata (può essere null). */
  originalParentPageId: string | null;
  /** order_index originale della pagina tra i suoi fratelli. */
  originalOrderIndex: number;
  /** ID dei fratelli originali (compreso il nodo spostato), in ordine. */
  originalSiblingIds: string[];
  /**
   * (NUOVO, Blocco 1) ID del record `page_move_history` lato server.
   * Quando presente, l'undo viene eseguito via RPC `undo_page_move` (atomico,
   * con snapshot persistente). I record creati prima dell'introduzione del
   * sistema RPC non hanno questo campo: per loro l'undo non è più possibile
   * dopo un refresh (la finestra in-memory è già scaduta) ma resta ok per
   * la sessione corrente tramite il path legacy (mantenuto per compat).
   */
  historyId?: string;
  /** Token di idempotenza usato per la creazione (debug). */
  operationToken?: string;
}

/**
 * Sposta una pagina e tutti i suoi discendenti dalla sezione `sourceSectionId`
 * alla sezione `destSectionId`. Rinumera gli ordini in entrambe le sezioni.
 */
export async function movePageToSection({
  supabase,
  pageId,
  sourceSectionId,
  destSectionId,
  afterPageId = null,
  expectedCourseId = null,
}: MovePageToSectionParams): Promise<MovePageToSectionResult> {
  // ---------------------------------------------------------------------------
  // BLOCCO 1: percorso preferenziale via RPC server-side `execute_page_move`.
  // Vantaggi: atomico (transazione PL/pgSQL), lock per corso (no race),
  // sanity check post-move, snapshot persistente in `page_move_history` (90gg)
  // e idempotenza tramite operation_token.
  // ---------------------------------------------------------------------------
  const operationToken =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `tok-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Snapshot minimo per il path legacy / undo locale (parent + ordine fratelli).
  // Lo leggiamo PRIMA dell'RPC per garantire coerenza in caso di undo locale.
  const { data: srcLinksPre } = await supabase
    .from("section_pages")
    .select("page_id, order_index, pages!inner(parent_page_id)")
    .eq("section_id", sourceSectionId);
  type RowLite = { page_id: string; order_index: number; parent_page_id: string | null };
  const srcRowsPre: RowLite[] = (srcLinksPre || []).map((r: any) => ({
    page_id: r.page_id,
    order_index: r.order_index,
    parent_page_id: r.pages?.parent_page_id ?? null,
  }));
  const movedRootPre = srcRowsPre.find((r) => r.page_id === pageId);
  const originalParent = movedRootPre?.parent_page_id ?? null;
  const originalOrder = movedRootPre?.order_index ?? 0;
  const originalSiblings = srcRowsPre
    .filter((r) => r.parent_page_id === originalParent)
    .sort((a, b) => a.order_index - b.order_index)
    .map((r) => r.page_id);

  // BLOCCO 2: chiamata avvolta in retry con backoff. L'idempotenza tramite
  // `operation_token` rende sicuro ripetere la chiamata in caso di rete instabile.
  const { data: rpcData, error: rpcError } = await withRpcRetry<{ data: any; error: any }>(() =>
    supabase.rpc("execute_page_move", {
      p_page_id: pageId,
      p_source_section_id: sourceSectionId,
      p_dest_section_id: destSectionId,
      p_after_page_id: afterPageId ?? null,
      p_expected_course_id: expectedCourseId ?? null,
      p_operation_token: operationToken,
    }) as any,
  );

  if (rpcError) {
    // Mappa errori server (codici prefissati) in messaggi italiani parlanti.
    const raw = rpcError.message || "";
    let msg = raw;
    if (raw.includes("SAME_SECTION")) msg = "La sezione di destinazione coincide con quella di origine.";
    else if (raw.includes("CROSS_COURSE")) msg = "Le sezioni di origine e destinazione non appartengono allo stesso corso.";
    else if (raw.includes("PAGE_NOT_IN_SOURCE")) msg = "La pagina non appartiene alla sezione di origine indicata.";
    else if (raw.includes("CYCLE_DETECTED")) msg = "Non posso posizionare la pagina dopo se stessa o un suo discendente.";
    else if (raw.includes("SANITY_FAILED")) msg = "Controllo di integrità post-spostamento fallito: l'operazione è stata annullata automaticamente.";
    else if (raw.includes("PERMISSION_DENIED")) msg = "Non ho i permessi per spostare pagine (serve ruolo autore o superiore).";
    throw new Error(msg);
  }

  const result = rpcData as {
    history_id: string;
    operation_token: string;
    moved_count: number;
    idempotent_replay: boolean;
  };

  return {
    movedCount: result.moved_count,
    undo: {
      pageId,
      destSectionId,
      originalSectionId: sourceSectionId,
      originalParentPageId: originalParent,
      originalOrderIndex: originalOrder,
      originalSiblingIds: originalSiblings,
      historyId: result.history_id,
      operationToken: result.operation_token,
    },
  };
}

/**
 * @deprecated Path legacy JS-side (pre-Blocco 1). Mantenuto solo come backup
 * teorico; non più chiamato. Da rimuovere in una release futura una volta
 * validato il funzionamento dell'RPC in produzione.
 */
export async function movePageToSectionLegacy({
  supabase,
  pageId,
  sourceSectionId,
  destSectionId,
  afterPageId = null,
  expectedCourseId = null,
}: MovePageToSectionParams): Promise<MovePageToSectionResult> {
  // ---------------------------------------------------------------------------
  // 0) VALIDAZIONI di input chiare e parlanti.
  // ---------------------------------------------------------------------------
  if (!pageId || !sourceSectionId || !destSectionId) {
    throw new Error("Parametri mancanti: pageId, sourceSectionId e destSectionId sono obbligatori.");
  }
  if (sourceSectionId === destSectionId) {
    throw new Error("Lo spostamento non è possibile: la sezione di destinazione coincide con quella di origine.");
  }
  if (expectedCourseId) {
    // Verifica che entrambe le sezioni appartengano allo stesso corso.
    const { data: secs, error: secErr } = await supabase
      .from("sections")
      .select("id, modules!inner(course_id)")
      .in("id", [sourceSectionId, destSectionId]);
    if (secErr) throw new Error(`Impossibile verificare le sezioni: ${secErr.message}`);
    const rows = (secs || []) as Array<{ id: string; modules: { course_id: string } | { course_id: string }[] }>;
    if (rows.length !== 2) {
      throw new Error("Sezione di origine o di destinazione non trovata.");
    }
    for (const r of rows) {
      const cid = Array.isArray(r.modules) ? r.modules[0]?.course_id : r.modules?.course_id;
      if (cid !== expectedCourseId) {
        throw new Error("Le sezioni di origine e destinazione non appartengono allo stesso corso.");
      }
    }
  }

  // ---------------------------------------------------------------------------
  // 1) Carica TUTTE le pagine della sezione di origine (id + parent_page_id)
  //    via section_pages: ci serve per calcolare il sotto-albero della pagina
  //    da spostare e per ricostruire la lista dei fratelli rimasti.
  // ---------------------------------------------------------------------------
  const { data: srcLinks, error: srcLinksErr } = await supabase
    .from("section_pages")
    .select("page_id, order_index, pages!inner(id, parent_page_id)")
    .eq("section_id", sourceSectionId);
  if (srcLinksErr) throw srcLinksErr;

  type Row = { page_id: string; order_index: number; parent_page_id: string | null };
  const srcRows: Row[] = (srcLinks || []).map((r: any) => ({
    page_id: r.page_id,
    order_index: r.order_index,
    parent_page_id: r.pages?.parent_page_id ?? null,
  }));

  const movedRoot = srcRows.find((r) => r.page_id === pageId);
  if (!movedRoot) {
    throw new Error("La pagina non appartiene alla sezione di origine indicata.");
  }
  const movedRootOriginalParent = movedRoot.parent_page_id;
  const movedRootOriginalOrderIndex = movedRoot.order_index;

  // Snapshot dell'ordine originale dei fratelli (per l'undo).
  const originalSiblingIds = srcRows
    .filter((r) => r.parent_page_id === movedRootOriginalParent)
    .sort((a, b) => a.order_index - b.order_index)
    .map((r) => r.page_id);

  // BFS sul sotto-albero: collect tutti i discendenti via parent_page_id
  const childrenByParent = new Map<string | null, string[]>();
  for (const r of srcRows) {
    const arr = childrenByParent.get(r.parent_page_id) ?? [];
    arr.push(r.page_id);
    childrenByParent.set(r.parent_page_id, arr);
  }
  const subtreeIds: string[] = [];
  const queue: string[] = [pageId];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    subtreeIds.push(cur);
    const children = childrenByParent.get(cur) ?? [];
    for (const c of children) queue.push(c);
  }
  const subtreeSet = new Set(subtreeIds);

  // ---------------------------------------------------------------------------
  // 2) Carica le pagine ROOT (parent_page_id IS NULL) della destinazione per
  //    sapere come renumerare e dove inserire il nodo spostato.
  // ---------------------------------------------------------------------------
  const { data: destLinks, error: destLinksErr } = await supabase
    .from("section_pages")
    .select("page_id, order_index, pages!inner(id, parent_page_id)")
    .eq("section_id", destSectionId);
  if (destLinksErr) throw destLinksErr;

  const destRoots = (destLinks || [])
    .filter((r: any) => (r.pages?.parent_page_id ?? null) === null)
    .map((r: any) => ({ page_id: r.page_id as string, order_index: r.order_index as number }))
    .sort((a, b) => a.order_index - b.order_index)
    .map((r) => r.page_id);

  // ---------------------------------------------------------------------------
  // 3) Sposta le righe section_pages del sotto-albero da source a dest.
  //    Una riga per pagina: aggiorniamo `section_id` mantenendo `is_linked`.
  //    Usiamo order_index temporanei (offset 10000+) per evitare collisioni con
  //    eventuali vincoli unique futuri; verranno riscritti dal renumber.
  // ---------------------------------------------------------------------------
  for (let i = 0; i < subtreeIds.length; i++) {
    const pid = subtreeIds[i];
    const { error } = await supabase
      .from("section_pages")
      .update({ section_id: destSectionId, order_index: 10000 + i })
      .eq("section_id", sourceSectionId)
      .eq("page_id", pid);
    if (error) throw error;
  }

  // ---------------------------------------------------------------------------
  // 4) Aggiorna pages: la radice diventa root nella destinazione (parent=null)
  //    e tutti i nodi del sotto-albero hanno section_id = dest.
  // ---------------------------------------------------------------------------
  {
    const { error } = await supabase
      .from("pages")
      .update({ parent_page_id: null, section_id: destSectionId })
      .eq("id", pageId);
    if (error) throw error;
  }
  if (subtreeIds.length > 1) {
    const descendantIds = subtreeIds.filter((id) => id !== pageId);
    const { error } = await supabase
      .from("pages")
      .update({ section_id: destSectionId })
      .in("id", descendantIds);
    if (error) throw error;
  }

  // ---------------------------------------------------------------------------
  // 5) Rinumera fratelli RIMASTI nella sezione di origine (stesso parent
  //    originale del nodo spostato), in modo che le numerazioni gerarchiche
  //    1.x.y restino contigue (0..N).
  // ---------------------------------------------------------------------------
  const remainingSiblings = srcRows
    .filter((r) => r.parent_page_id === movedRootOriginalParent && !subtreeSet.has(r.page_id))
    .sort((a, b) => a.order_index - b.order_index)
    .map((r) => r.page_id);
  for (let i = 0; i < remainingSiblings.length; i++) {
    const pid = remainingSiblings[i];
    const { error: spErr } = await supabase
      .from("section_pages")
      .update({ order_index: i })
      .eq("section_id", sourceSectionId)
      .eq("page_id", pid);
    if (spErr) throw spErr;
    const { error: pErr } = await supabase
      .from("pages")
      .update({ order_index: i })
      .eq("id", pid);
    if (pErr) throw pErr;
  }

  // ---------------------------------------------------------------------------
  // 6) Costruisce il nuovo ordine delle ROOT della destinazione e rinumera.
  //    Inserisce il nodo spostato dopo `afterPageId` se presente, altrimenti
  //    in coda.
  // ---------------------------------------------------------------------------
  let insertPos = destRoots.length;
  if (afterPageId) {
    const idx = destRoots.findIndex((id) => id === afterPageId);
    if (idx >= 0) insertPos = idx + 1;
  }
  const newDestOrder = [
    ...destRoots.slice(0, insertPos),
    pageId,
    ...destRoots.slice(insertPos),
  ];
  for (let i = 0; i < newDestOrder.length; i++) {
    const pid = newDestOrder[i];
    const { error: spErr } = await supabase
      .from("section_pages")
      .update({ order_index: i })
      .eq("section_id", destSectionId)
      .eq("page_id", pid);
    if (spErr) throw spErr;
    const { error: pErr } = await supabase
      .from("pages")
      .update({ order_index: i })
      .eq("id", pid);
    if (pErr) throw pErr;
  }

  // I discendenti spostati conservano i loro order_index relativi tra fratelli
  // (sono stati portati nella destinazione con offset 10000+i, ma la lettura
  // li raggruppa per parent_page_id, quindi l'ordine relativo è preservato).
  // Rinumeriamo comunque i loro order_index a 0..N per ciascun gruppo di
  // fratelli del sotto-albero, per pulizia.
  const childrenByParentInSubtree = new Map<string, string[]>();
  for (const id of subtreeIds) {
    if (id === pageId) continue;
    const parent = srcRows.find((r) => r.page_id === id)?.parent_page_id;
    if (!parent) continue;
    const arr = childrenByParentInSubtree.get(parent) ?? [];
    arr.push(id);
    childrenByParentInSubtree.set(parent, arr);
  }
  for (const [, ids] of childrenByParentInSubtree) {
    // Ordina secondo l'order_index originale nella sorgente
    const sorted = ids
      .map((id) => ({ id, oi: srcRows.find((r) => r.page_id === id)?.order_index ?? 0 }))
      .sort((a, b) => a.oi - b.oi)
      .map((x) => x.id);
    for (let i = 0; i < sorted.length; i++) {
      const pid = sorted[i];
      const { error: spErr } = await supabase
        .from("section_pages")
        .update({ order_index: i })
        .eq("section_id", destSectionId)
        .eq("page_id", pid);
      if (spErr) throw spErr;
      const { error: pErr } = await supabase
        .from("pages")
        .update({ order_index: i })
        .eq("id", pid);
      if (pErr) throw pErr;
    }
  }

  return {
    movedCount: subtreeIds.length,
    undo: {
      pageId,
      destSectionId,
      originalSectionId: sourceSectionId,
      originalParentPageId: movedRootOriginalParent,
      originalOrderIndex: movedRootOriginalOrderIndex,
      originalSiblingIds,
    },
  };
}

// =============================================================================
// PREVIEW: simula gli order_index risultanti senza scrivere nel DB.
// =============================================================================

export interface PreviewParams {
  supabase: SupabaseClient<any>;
  pageId: string;
  sourceSectionId: string;
  destSectionId: string;
  afterPageId?: string | null;
}

export interface PreviewRow {
  page_id: string;
  title: string;
  /** Posizione 0-based tra i fratelli del proprio parent (nuova). */
  newOrderIndex: number;
  /** Posizione 0-based attuale (PRIMA dello spostamento), se presente. */
  oldOrderIndex: number | null;
  /** True se è il nodo radice spostato. */
  isMovedRoot: boolean;
}

export interface PreviewResult {
  /** Fratelli RIMASTI nella sezione di origine (al livello del nodo spostato). */
  sourceAfter: PreviewRow[];
  /** Pagine ROOT della sezione di destinazione, includendo il nodo spostato. */
  destAfter: PreviewRow[];
  /** Numero totale di nodi che verranno spostati (radice + discendenti). */
  movedCount: number;
}

/**
 * Calcola, senza scrivere nulla, l'anteprima della numerazione (order_index)
 * per la sezione di origine e per la sezione di destinazione DOPO un ipotetico
 * spostamento di `pageId` (e del suo intero sotto-albero) da `sourceSectionId`
 * a `destSectionId`. Utile per mostrare un riepilogo all'utente prima di
 * confermare l'operazione.
 */
export async function previewMovePageToSection({
  supabase,
  pageId,
  sourceSectionId,
  destSectionId,
  afterPageId = null,
}: PreviewParams): Promise<PreviewResult> {
  if (sourceSectionId === destSectionId) {
    throw new Error("La sezione di destinazione coincide con quella di origine.");
  }

  // Sorgente: pagine con titolo e parent.
  const { data: srcLinks, error: srcErr } = await supabase
    .from("section_pages")
    .select("page_id, order_index, pages!inner(id, parent_page_id, title)")
    .eq("section_id", sourceSectionId);
  if (srcErr) throw srcErr;
  type Row = { page_id: string; order_index: number; parent_page_id: string | null; title: string };
  const srcRows: Row[] = (srcLinks || []).map((r: any) => ({
    page_id: r.page_id,
    order_index: r.order_index,
    parent_page_id: r.pages?.parent_page_id ?? null,
    title: r.pages?.title ?? "",
  }));
  const movedRoot = srcRows.find((r) => r.page_id === pageId);
  if (!movedRoot) {
    throw new Error("La pagina non appartiene alla sezione di origine indicata.");
  }

  // Calcola sotto-albero (BFS) per escludere i discendenti dalla sorgente.
  const childrenByParent = new Map<string | null, string[]>();
  for (const r of srcRows) {
    const arr = childrenByParent.get(r.parent_page_id) ?? [];
    arr.push(r.page_id);
    childrenByParent.set(r.parent_page_id, arr);
  }
  const subtreeIds: string[] = [];
  const queue: string[] = [pageId];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    subtreeIds.push(cur);
    for (const c of childrenByParent.get(cur) ?? []) queue.push(c);
  }
  const subtreeSet = new Set(subtreeIds);

  const remainingSiblings = srcRows
    .filter((r) => r.parent_page_id === movedRoot.parent_page_id && !subtreeSet.has(r.page_id))
    .sort((a, b) => a.order_index - b.order_index);
  const sourceAfter: PreviewRow[] = remainingSiblings.map((r, i) => ({
    page_id: r.page_id,
    title: r.title,
    newOrderIndex: i,
    oldOrderIndex: r.order_index,
    isMovedRoot: false,
  }));

  // Destinazione: pagine ROOT.
  const { data: destLinks, error: destErr } = await supabase
    .from("section_pages")
    .select("page_id, order_index, pages!inner(id, parent_page_id, title)")
    .eq("section_id", destSectionId);
  if (destErr) throw destErr;
  const destRows = (destLinks || []).map((r: any) => ({
    page_id: r.page_id as string,
    order_index: r.order_index as number,
    parent_page_id: (r.pages?.parent_page_id ?? null) as string | null,
    title: (r.pages?.title ?? "") as string,
  }));
  const destRoots = destRows
    .filter((r) => r.parent_page_id === null)
    .sort((a, b) => a.order_index - b.order_index);

  let insertPos = destRoots.length;
  if (afterPageId) {
    const idx = destRoots.findIndex((r) => r.page_id === afterPageId);
    if (idx >= 0) insertPos = idx + 1;
  }
  const newDestList = [
    ...destRoots.slice(0, insertPos),
    { page_id: pageId, order_index: -1, parent_page_id: null, title: movedRoot.title },
    ...destRoots.slice(insertPos),
  ];
  const destAfter: PreviewRow[] = newDestList.map((r, i) => ({
    page_id: r.page_id,
    title: r.title,
    newOrderIndex: i,
    oldOrderIndex: r.page_id === pageId ? null : r.order_index,
    isMovedRoot: r.page_id === pageId,
  }));

  return { sourceAfter, destAfter, movedCount: subtreeIds.length };
}

// =============================================================================
// UNDO: ripristina la collocazione originale (sezione + parent + posizione).
// =============================================================================

export interface UndoParams {
  supabase: SupabaseClient<any>;
  undo: MoveUndoInfo;
}

/**
 * Annulla uno spostamento eseguito in precedenza riportando la pagina (e tutto
 * il suo sotto-albero) nella sezione originale, ripristinando il
 * `parent_page_id` originale e la sua posizione tra i fratelli (order_index).
 *
 * Idempotenza: se la pagina è già nella sezione originale viene comunque
 * tentato il ripristino di parent + ordine.
 */
export async function undoMovePageToSection({
  supabase,
  undo,
}: UndoParams): Promise<MovePageToSectionResult> {
  // ---------------------------------------------------------------------------
  // BLOCCO 1: se abbiamo un `historyId`, deleghiamo l'undo all'RPC server-side
  // `undo_page_move` che usa lo snapshot persistente (atomico + lock).
  // ---------------------------------------------------------------------------
  if (undo.historyId) {
    // BLOCCO 2: undo con retry. La funzione server è ora idempotente:
    // un secondo undo sullo stesso history_id restituisce already_undone=true
    // anziché sollevare ALREADY_UNDONE, quindi un retry post-timeout è sicuro.
    const { error: rpcErr } = await withRpcRetry<{ data: any; error: any }>(() =>
      supabase.rpc("undo_page_move", { p_history_id: undo.historyId }) as any,
    );
    if (rpcErr) {
      const raw = rpcErr.message || "";
      let msg = raw;
      if (raw.includes("HISTORY_NOT_FOUND")) msg = "Il record dello spostamento non è più disponibile (potrebbe essere stato eliminato dalla pulizia automatica).";
      else if (raw.includes("PERMISSION_DENIED")) msg = "Non ho i permessi per annullare lo spostamento.";
      throw new Error(msg);
    }
    return {
      movedCount: 1,
      undo: {
        pageId: undo.pageId,
        destSectionId: undo.originalSectionId,
        originalSectionId: undo.destSectionId,
        originalParentPageId: null,
        originalOrderIndex: 0,
        originalSiblingIds: undo.originalSiblingIds,
      },
    };
  }

  // ---------------------------------------------------------------------------
  // FALLBACK LEGACY: nessun historyId disponibile (record creato prima
  // dell'introduzione dell'RPC, o RPC non disponibile). Eseguiamo l'undo
  // riusando il path JS-side. Sicuro perché non scrive nulla di nuovo che
  // l'RPC non gestisca.
  // ---------------------------------------------------------------------------
  const { pageId, destSectionId, originalSectionId, originalParentPageId, originalSiblingIds } = undo;

  // 1) Riporta la pagina (e sotto-albero) nella sezione originale come ROOT.
  let movedCount = 0;
  if (destSectionId !== originalSectionId) {
    // Usiamo esplicitamente il path JS-side legacy: l'RPC server-side
    // creerebbe un nuovo record di history per ogni undo, sporcando l'audit.
    const result = await movePageToSectionLegacy({
      supabase,
      pageId,
      sourceSectionId: destSectionId,
      destSectionId: originalSectionId,
    });
    movedCount = result.movedCount;
  }

  // 2) Ripristina il parent_page_id originale (anche se era root, per coerenza).
  {
    const { error } = await supabase
      .from("pages")
      .update({ parent_page_id: originalParentPageId })
      .eq("id", pageId);
    if (error) throw new Error(`Annullamento fallito (parent): ${error.message}`);
  }

  // 3) Rinumera il gruppo di fratelli ricostruito (i fratelli originali ora
  //    presenti nella sezione originale, riposizionando il nodo spostato
  //    nell'ordine snapshot `originalSiblingIds`).
  const { data: nowLinks, error: nowErr } = await supabase
    .from("section_pages")
    .select("page_id, order_index, pages!inner(id, parent_page_id)")
    .eq("section_id", originalSectionId);
  if (nowErr) throw new Error(`Annullamento fallito (lettura): ${nowErr.message}`);
  const presentIds = new Set(
    (nowLinks || [])
      .filter((r: any) => (r.pages?.parent_page_id ?? null) === originalParentPageId)
      .map((r: any) => r.page_id as string),
  );
  // Mantieni l'ordine originale, filtra a quelli ancora presenti, aggiungi in
  // coda eventuali nuovi fratelli (creati nel frattempo).
  const ordered: string[] = originalSiblingIds.filter((id) => presentIds.has(id));
  for (const id of presentIds) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  for (let i = 0; i < ordered.length; i++) {
    const pid = ordered[i];
    const { error: spErr } = await supabase
      .from("section_pages")
      .update({ order_index: i })
      .eq("section_id", originalSectionId)
      .eq("page_id", pid);
    if (spErr) throw new Error(`Annullamento fallito (riordino): ${spErr.message}`);
    const { error: pErr } = await supabase
      .from("pages")
      .update({ order_index: i })
      .eq("id", pid);
    if (pErr) throw new Error(`Annullamento fallito (pagina): ${pErr.message}`);
  }

  // L'undo non produce un nuovo undo significativo: restituiamo undo "inverso"
  // di comodo (puntando alla sezione originale come destinazione finale).
  return {
    movedCount: movedCount || 1,
    undo: {
      pageId,
      destSectionId: originalSectionId,
      originalSectionId: destSectionId,
      originalParentPageId: null,
      originalOrderIndex: 0,
      originalSiblingIds: ordered,
    },
  };
}